import { useCallback } from "react";
import { Property } from "src/interfaces/IProperty";
import { Floor, Zone } from "src/interfaces/IZone";
import { ClientDetails } from "src/interfaces/IClient";
import useAlenRooms from "src/services/alen/useAlenRooms";
import {
  mapAlenRoomsToWyndFloors,
  mergeAlenFloors,
} from "src/services/alen/utils";
import { useWyndZoneQuery } from "./DataFetching/floor";
import { getFloorsOfZones } from "./utils";
import { SelectedValue } from "./types";
import useCombinedProperty from "./DataFetching/property";

export type Options = {
  properties: Property[];
  floors: Floor[];
  zones: Zone[];
};

const getSelectedProperty = (properties: Property[], propertyName: string) => {
  const property = properties.find((p) => p.property_name === propertyName);
  return property;
};

const useSelectorOptions = ({
  client,
  selected,
}: {
  client: ClientDetails;
  selected: SelectedValue;
}): {
  data: Options;
  loading: boolean;
  refetchZone: () => Promise<void>;
} => {
  const allProperties = useCombinedProperty({ client });
  const selectedProperty = getSelectedProperty(
    allProperties.data ?? [],
    selected.propertyName ?? ""
  );

  const alenRoomsQuery = useAlenRooms(selectedProperty?.alen_id);
  const allUserZones = useWyndZoneQuery(client);

  const handleRefetchZone = useCallback(async (): Promise<void> => {
    await Promise.allSettled([
      alenRoomsQuery.refetch(),
      allUserZones.refetch(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const zonesOfProperty =
    !selectedProperty || !allUserZones.data
      ? undefined
      : allUserZones.data.filter(
          (z: Zone) => z.property_id === selectedProperty.property_id
        );
  const wyndZoneOfFloor = zonesOfProperty
    ? getFloorsOfZones(zonesOfProperty)
    : undefined;

  const alenFloors = alenRoomsQuery.data
    ? alenRoomsQuery.data.map((r) => mapAlenRoomsToWyndFloors(r))
    : undefined;

  const floors =
    wyndZoneOfFloor && alenFloors
      ? mergeAlenFloors(wyndZoneOfFloor, alenFloors)
      : [];

  const zones =
    zonesOfProperty && selected.floors.length > 0
      ? zonesOfProperty.filter((z) =>
          selected.floors.map((f) => f.name).includes(z.floor)
        )
      : [];

  return {
    data: {
      properties: allProperties.data ?? [],
      floors,
      zones,
    },
    loading: alenRoomsQuery.loading,
    refetchZone: handleRefetchZone,
  };
};

export default useSelectorOptions;
